// Core data types for procedural scenery (Set13). Scenery is a SECOND placement system, a sibling
// of (not an extension to) the Set9 procedural-material pipeline. Materials answer "what colour is
// the ground at world point p?"; scenery answers "what discrete objects stand on the ground, and
// where?". The two are ORTHOGONAL by design: a park with scattered pines over mown grass must be
// authorable without changing the ground material, and a forest-floor ground material must be
// authorable without placing a single tree.
//
// Terminology: the system is **scenery**; one authored type is a **scenery kind**
// (`SceneryKindDef`); one derived structural flavour of a kind is a **variant**; one placed object
// is an **instance**. "Scatter" is deliberately NOT used (the material primitive owns that word).
//
// Data model: deliberately ISOMORPHIC to the material system. `ParamSchema` is imported verbatim
// from `@MaterialTypes.ts` (NOT re-declared) — that single decision recovers the auto-generated
// UI, the DB storage shape, the min/max validation, and the extend/fork semantics. Scenery adds
// exactly ONE new idea: a param may declare a per-variant **swing** (§4.3), which lives beside its
// default in the schema (`ParamSchemaWithSpread`) and is overridable like anything else through
// the single `paramOverrides` map.
//
// This module is DB-FREE (no mobx-graphlink / web-vcore / @apollo/client), so the Node test lane
// can import it directly (the @MaterialFeature.ts precedent). All imports use `.js` extensions.
import type {ParamSchema, ParamValue} from "../Materials/@MaterialTypes.js";
export type {ParamValue} from "../Materials/@MaterialTypes.js";

/** A scenery generator's input: a variant's fully-resolved flat param map (keyed by the kind's
 *  declared param keys). Set14 P3 §4.6 — the per-variant seed is passed separately. Shared across
 *  the generators (was `Gen_PineTree`'s local type; moved here when the pine was deleted in P5). */
export interface GenParams {
	[key: string]: ParamValue;
}

/** A param schema that may also declare a per-variant SWING.  An INTERSECTION type rather than a
 *  new field on `ParamSchema` itself: the base class stays untouched (the material system has no
 *  variants and would carry a permanently-ignored field), while scenery still gets one
 *  declaration site per param — no parallel map to keep in sync (§4.3 b).
 *  Absent `swingDefault` = the param does not vary; its value is identical in every variant. */
export type ParamSchemaWithSpread = ParamSchema & {
	/** HALF-WIDTH of the per-variant variation, in the param's own units: the span is
	 *  `value ± swing`.  Half-width, not full-width, so the arithmetic stays obvious. */
	swingDefault?: number|n;
	/** "log" applies the swing GEOMETRICALLY (value × / ÷ rather than + / −) — right for scales
	 *  and radii spanning an order of magnitude.  Same mul-vs-add distinction as `ModOp`, so it is
	 *  ONE field: "log" IS the proportional case (no separate swingOp). */
	curveDefault?: "linear" | "log" | n;
};

/** A PATCH over one param's resolved (value, swing, curve) — the value type of `paramOverrides`.
 *  EVERY FIELD IS OPTIONAL AND ONLY THE PRESENT ONES APPLY: this is a partial override, never a
 *  replacement.  So `{value: 18}` retouches the value and leaves the inherited swing/curve alone,
 *  and `{swing: 0}` collapses an inherited variation without touching the value.
 *  Mirrors `ModRef` term-for-term: ModRef's `base` ↔ our `value`, `amount` ↔ `swing`, `op` ↔
 *  `curve`.  The UI labels the swing slider "swing". */
export interface ParamSpread {
	value?: ParamValue|n;
	swing?: number|n;
	curve?: "linear" | "log" | n;
}

/** A bare value in a `paramOverrides` entry is treated as `{value: <bare>}` (the recipe-shaped
 *  shorthand — the common case is byte-identical in shape to a material recipe's). */
export type ParamOverride = ParamSpread | ParamValue;

/** Normalise a `ParamOverride` to a `ParamSpread` partial patch.  A bare value becomes
 *  `{value: <bare>}`; a `ParamSpread` passes through; null/undefined becomes `{}`.  The `value`
 *  field is the only one that can hold a bare non-object value, so a NON-`ParamSpread` object
 *  (a color/ramp value) is wrapped as `{value: <it>}` too.  Shared by the resolver and the UI so
 *  they can never disagree about what a patch means. */
export function toParamSpread(o: ParamOverride|n|undefined): ParamSpread {
	if (o == null) return {};
	if (typeof o != "object") return {value: o};
	if ("value" in o || "swing" in o || "curve" in o) return o as ParamSpread;
	return {value: o as ParamValue};
}

/** Apply a partial `ParamSpread` patch ON TOP of an existing override, returning a NEW override.
 *  ONLY the present fields of `patch` win — the existing value/swing/curve are preserved unless
 *  the patch names them.  An explicit `undefined` field clears it.  Returns `undefined` when the
 *  merged patch would be entirely empty (nothing present) — the UI drops such an entry. */
export function mergeParamOverride(existing: ParamOverride|n|undefined, patch: ParamSpread): ParamOverride|undefined {
	const base = toParamSpread(existing);
	const merged: ParamSpread = {...base};
	for (const k of ["value", "swing", "curve"] as const) {
		if (k in patch) {
			if (patch[k] == null) delete merged[k];
			else (merged as any)[k] = patch[k];
		}
	}
	// Drop the entry if nothing is present (the UI deletes the map key).
	if (merged.value == null && merged.swing == null && merged.curve == null) return undefined;
	return merged;
}

/** Read the EFFECTIVE value of an override (the resolved value a `deriveVariants`-style consumer
 *  sees), falling back to the param's schema default when the patch doesn't touch `value`. */
export function effectiveParamValue(o: ParamOverride|n|undefined, fallback: ParamValue): ParamValue {
	const s = toParamSpread(o);
	return s.value != null ? s.value : fallback;
}

/** Read the EFFECTIVE swing of an override, falling back to `fallback` (the resolved swing). */
export function effectiveParamSwing(o: ParamOverride|n|undefined, fallback: number): number {
	const s = toParamSpread(o);
	return typeof s.swing == "number" ? s.swing : fallback;
}

/** Scalar helper for extracting a numeric value from a param (used by `deriveVariants`).
 *  Scenery's swung params are scalars; color/ramp params are never swung (their default is used
 *  verbatim).  Returns the number or `undefined` when the value is non-numeric. */
export function asNumber(v: ParamValue|n|undefined): number|n {
	if (typeof v == "number" && Number.isFinite(v)) return v;
	return undefined;
}

/** One derived structural flavour of a kind: the fully-resolved param set + its atlas cell index.
 *  See `deriveVariants` (Terrain/Scenery/SceneryVariants.ts). */
export interface SceneryVariant {
	index: number;
	/** Every param resolved to a concrete value: the resolved value itself for params with no
	 *  swing, this variant's sampled stratum for the rest — the generator's input. */
	params: {[key: string]: ParamValue};
	/** Relative selection frequency (normalised across the kind's variants). */
	weight: number;
}

/** A scenery kind definition — the ONLY authored artifact.  Everything else (variants, meshes,
 *  atlas, quads) is derived from this.  Isomorphic to `MaterialRecipe` (see the Set13 §3.1 table):
 *  `recipeID` ↔ `kindID`, `ParamSchema` ↔ `ParamSchemaWithSpread`, `paramOverrides` (values) ↔
 *  `paramOverrides` (partial `ParamSpread` patches). */
export class SceneryKindDef {
	constructor(data?: Partial<SceneryKindDef>) { Object.assign(this, data); }
	id = "";
	name = "";
	/** Which generator builds the mesh. A REGISTRY ID, never a closed union (the Set9 D10
	 *  "no recipeKind union" decision) — a new kind is data + one generator function. */
	generatorID = "";
	/** n = root def. Set = live `extend` link (the D10 chain-walk semantics, verbatim). */
	baseKindID?: string|n;

	/** The generator's exposed knobs — DRIVES THE UI.  Each schema carries the param's `default`
	 *  value AND (optionally) its `swingDefault`/`curveDefault`: ONE declaration site per param,
	 *  exactly as for recipes.  A built-in root therefore declares each param ONCE and leaves
	 *  `paramOverrides` empty. */
	params: ParamSchemaWithSpread[] = [];
	/** SPARSE, keyed by the base chain's declared param keys; derived wins.  An entry may be a
	 *  bare VALUE (the common case, byte-identical in shape to a recipe's) or a partial
	 *  {@link ParamSpread} patch touching only the fields it names (§4.3 b). */
	paramOverrides: {[paramKey: string]: ParamOverride} = {};
	/** How many structural variants to derive. Half a dozen is plenty (§3.2). */
	variantCount = 6;
	/** Optional relative frequencies (length must equal variantCount). Absent = uniform. */
	variantWeights?: number[]|n;
	/** Decorrelates this def's variant sampling + placement hashes from every other def's. */
	seed = 0;

	// ── Kind-agnostic fields the PLACEMENT layer needs (rocks and trees differ here) ──
	/** "up" = always vertical (trees); "surfaceNormal" = lie along the slope (boulders);
	 *  "blend" = partway. v1 top-down 2D ignores this; it is read by the bake + future 3D. */
	groundAlign: "up" | "surfaceNormal" | "blend" = "up";
	/** Metres the object sinks into the ground (boulders are partly buried; trees ~0).
	 *  ⚠ Set14 P3 — now NULLABLE (default `null`): `null` = NO adjustment (the generator's native
	 *  min-Z is used as-is — a `Bush 1` that dips below the origin plane keeps its intentional
	 *  skirt); a NUMBER = auto-ground first (translate so measured min-Z = 0), THEN sink by this
	 *  amount (§4.2 c). This is a NUMERIC field compared against — a bare `>`/`<` against it is
	 *  `false` for `null` (Set13 §8's top hazard). Branch on `== null` explicitly. */
	sinkDepth?: number|n;
	/** Ground-plane WIDTH relative to the kind's height — dimensionless. The resolved footprint
	 *  radius (metres) is `targetHeight × footprintRadiusRatio` (composed in the resolver), so
	 *  resizing a kind via its `targetHeight` param keeps its width proportional. Quad sizing
	 *  itself is measured from the bake (`cellWorldHalfExtent`); this ratio drives the placement
	 *  hints + signatures. */
	footprintRadiusRatio = 0.5;

	// ── Placement defaults (a PathSceneryFeature may override) ──
	/** Mean spacing in metres between placement candidates. Set17: a pure DENSITY knob — the
	 *  candidate lattice pitch for a region — DECOUPLED from collision (collision is expressed via
	 *  `collisionRadius` below). A sensible floor of ~2× the trunk radius keeps candidates from
	 *  being mostly-rejected by the arbiter, but that is a heuristic default, NOT a semantic
	 *  coupling. */
	spacing = 6;
	/** Fraction of candidate cells that actually spawn (the Set3 §4.1 presence gate). */
	presence = 0.8;
	/** Set17 — the per-kind collision shape used by the spatial-hash arbiter. Absent → derived
	 *  from `spacing` (trunk = 0.25×spacing, crown = 0.5×spacing unless the kind is ground cover,
	 *  see the resolver). */
	collision?: SceneryCollision|n;
	/** Instance-level (FREE, non-structural) jitter — see §3.2's two-axis rule. */
	jitter?: SceneryInstanceJitter|n;
}

/** Set17 — a kind's collision radii for the spatial-hash arbiter. The trunk/crown split captures
 *  the D2 semantic: a flower must avoid a tree's TRUNK (rule 1) but never its CROWN (rule 2 —
 *  ground cover lives under the canopy). */
export class SceneryCollision {
	constructor(data?: Partial<SceneryCollision>) { Object.assign(this, data); }
	/** Hard ground exclusion radius (m) — ALWAYS enforced against other kinds' trunk radii
	 *  (conflict rule 1). */
	trunk = 1.5;
	/** Canopy exclusion radius (m) — enforced only when BOTH parties have a non-null crown
	 *  (conflict rule 2). `null` = ground cover: opts out of canopy space entirely (a flower's
	 *  crown is null, so it never crown-collides with a pine's crown — but DOES trunk-collide with
	 *  the pine's trunk). */
	crown: number|null = 3;
}

/** Instance-level variation: applied at draw time from per-instance hashes. Costs NOTHING
 *  (no extra variant, no extra atlas cell) because it does not change the mesh. */
export class SceneryInstanceJitter {
	constructor(data?: Partial<SceneryInstanceJitter>) { Object.assign(this, data); }
	/** Full-circle rotation about up. For a top-down quad this is a UV rotation. */
	rotation = 1;
	/** Uniform scale multiplier, MIN and MAX of the per-instance range (lerped by a hash, not
	 *  value + swing) — so a kind can bias toward SHRINKING (targetHeight = mature height;
	 *  saplings are common, 2×-mature is not). Equal min/max = no per-instance height variation. */
	scale_min = 1;
	scale_max = 1;
	/** Per-instance albedo multiplier swing (0 = none). */
	tintSwing = 0.12;
}

/** One param after the extend chain + paramOverrides patches are applied — what `deriveVariants`
 *  actually samples from.  The resolution ORDER is: schema defaults, then each chain level's
 *  paramOverrides patch (root → leaf), each patch touching only the fields it names. */
export interface ResolvedSceneryParam {
	key: string;
	schema: ParamSchemaWithSpread;
	value: ParamValue;
	/** The value this param resolves to WITHOUT the current kind's (leaf's) own override — i.e.
	 *  "one inheritance level earlier" (root→parent overrides + the leaf's schema default).  What
	 *  an inherit/reset shows in the editor, distinct from `value` (which includes the leaf's own
	 *  `paramOverrides` patch when present).  Equal to `value` when the leaf doesn't override. */
	fallback: ParamValue;
	/** 0 / absent = does not vary between variants (§3.2 d: a non-zero swing is what makes a param
	 *  STRUCTURAL). */
	swing: number;
	curve: "linear" | "log";
}

/** A named group of params, in declaration order (Set14 P2 — `ParamSchema.group`). */
export interface ParamGroup {
	name: string;
	params: ResolvedSceneryParam[];
}

/** Partition resolved params into groups by `ParamSchema.group`, preserving first-appearance order
 *  of groups and declaration order within each group.  Params with no group land in "General".
 *  PURE + DB-free so the KindEditor and the test lane share one definition of "how groups form". */
export function groupSceneryParams(params: ResolvedSceneryParam[]): ParamGroup[] {
	const groups: ParamGroup[] = [];
	for (const p of params) {
		const name = p.schema.group || "General";
		const g = groups.find(g=>g.name == name);
		if (g) g.params.push(p);
		else groups.push({name, params: [p]});
	}
	return groups;
}

// ── The mesh contract (Set14 P3 §4.7) ─────────────────────────────────────────────────────────
// Moved HERE (shared across generator + worker + bake) from Gen_PineTree's local definition.
// ez-tree emits TWO geometries needing different bake treatment (bark: opaque, FrontSide; leaf:
// alpha-tested, DoubleSide, different texture) — the old single-mesh-single-albedo contract
// cannot express that, so a mesh is now an array of PARTS with a per-part material kind.

/** How one mesh part is baked/rendered. `bark` = opaque FrontSide trunk/branches; `leaf` =
 *  alpha-tested DoubleSide canopy. */
export type SceneryMaterialKind = "bark" | "leaf";

/** One discrete sub-mesh of a `SceneryMesh`. Indices are ALWAYS Uint32 (§2.3 — Uint16 silently
 *  wraps above 65 535 verts with no error; a foliage-heavy ez-tree leaf part already measures
 *  ~21 760 verts). Positions/normals/uvs are xyz per vertex (uvs = xy per vertex).
 *  Set14 P4 §4.5 — the transitional per-vertex `colors` are GONE (removed with the material
 *  swap): the bake's MeshStandardMaterial tints + textures replace the legacy per-vertex colour.
 *  The material-kind (`bark`/`leaf`) selects which material the bake renders the part with. */
export interface SceneryMeshPart {
	positions: Float32Array;
	normals: Float32Array;
	uvs: Float32Array;
	indices: Uint32Array;
	materialKind: SceneryMaterialKind;
}

/** The generator's output — what `Gen_*` builds from a resolved variant's params and the bake
 *  consumes. Z-up (X/Y ground, Z up) metres. Set14 P4 §4.5 — the transitional `albedo` is GONE
 *  (the bake's material tints + textures replace it). */
export interface SceneryMesh {
	parts: SceneryMeshPart[];
	/** Measured max Z (metres) — the honest silhouette height (feeds shadow shear + depth key). */
	maxZ: number;
	/** Measured ground-plane half-extent (metres) — feeds quad sizing + spacing hints. */
	halfExtentXY: number;
}
